import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from '@react-router';

import type { LayoutBaseProps } from '../layout.types';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { Box, Flex, NoSsr } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';

import { FrameWrapper } from './components/FrameWrapper';
import { MainFrame } from './components/MainFrame';
import { AuxFrameProvider } from './components/AuxFrame';
import { Body } from './components/Body';
import { alohaConfig, ALOHA_SIMPLE_CONTEXT, useAlohaSimpleCanvas, useAlohaDesign } from './LayoutAloha.theme';
import { ThemeSprites } from '@shared/utils/styleApplicator/ThemeSprites';
import { themeCanvasBoxProps } from '@shared/utils/styleApplicator/themeCanvasBoxProps';
import { WelcomeCard } from './components/WelcomeCard';
import { LazyWelcomeVideo } from '../../widgets/WelcomeVideo/WelcomeVideo.lazy';
import { Footer } from '@apps/guest/packages/layout-engine/components/Footer';
import { alohaFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { PageContainer, CustomPageContainer, StyledFlexAloha, FooterAnchorExcludedFlex } from './LayoutAloha.styles';
import { isInIframe } from '@shared/utils/isInIframe';
import { useLayoutAlohaController } from './LayoutAloha.controller';
import { AlohaScrollController } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/components/AlohaScrollController';
import type { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import { PreloadImages } from '@apps/guest/packages/layout-engine/components/PreloadPageImages/PreloadImages';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { DesignLayoutType, EventPageType } from '@graphql/generated-base';
import { useColorPaletteProvider } from '../ColorPaletteProvider/ColorPaletteProvider';
import { WebsiteHeader } from './components/WebsiteHeader/WebsiteHeader';
import type { PreparedPages } from './components/Body/Body.constants';
import { pageWidgets } from './components/Body/Body.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestSitePages } from '../../hooks/useGuestSitePages';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import { getMigratedPageSlug, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import queryString from 'query-string';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { getCustomArtMobileUrl, getCustomArtUrl, getEventMenuPropsFromEvent, getIsCustomArtEnabled } from '../layout.utils';
import { addRendition } from '@shared/utils/photoRendition';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useTranslation } from '@shared/core';
import { CustomArt } from './components/CustomArt/CustomArt';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useIsStaysRedesignActive } from '@apps/guest/packages/layout-engine/widgets/Custom/components/Honeymoon/useIsStaysRedesignActive';
import FooterNav from '../../components/FooterNav/FooterNav';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useWelcomePagePhotoUrl } from '../hooks/useWelcomePagePhotoUrl';
import { isDeepLinkTargetSlug, useDeepLinkTargetSlug } from '../hooks/useDeepLinkTargetSlug';
import { useShouldRevealPageContent } from '@apps/guest/packages/layout-engine/components/PageContentVisibilityProvider';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { isScrollAnchoringSupported } from './hooks/scrollAnchoring';
import { ALOHA_SCROLL_CONTAINER_ID } from '@apps/guest/routes/GuestSite/GuestSite.constants';
import { GuestLanguageSelectorFloating } from '@apps/guest/components/GuestLanguageSelector';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { MobileTopBarNav, MobileTopBarNavOffsetProvider } from '@apps/guest/packages/layout-engine/components/MobileTopBarNav';
import { TopBarNavEventMenu } from '@apps/guest/packages/layout-engine/components/TopBarNavEventMenu';
import { BulkCustomPageDataProvider } from '@apps/guest/packages/layout-engine/widgets/Custom/BulkCustomPageDataProvider';

export interface LayoutAlohaProps extends Readonly<LayoutBaseProps> {
  setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
  setIsLastPageVisible?: (value: boolean) => void;
  setIsPastFirstPage?: (value: boolean) => void;
}

const { simple } = alohaConfig.applicatorHtmlProps;

// Shared empty object so a missing applicator prop doesn't defeat Body's memo with a fresh {}.
const EMPTY_APPLICATOR_PROPS = {};

const LayoutAloha: React.FC<LayoutAlohaProps> = ({ eventHandle, event, setLoadingTheme, setIsLastPageVisible, setIsPastFirstPage, hideNav, ...restProps }) => {
  const {
    id,
    pages,
    photo,
    mobilePhoto,
    video,
    info: { eventDisplayName, greeting },
  } = event;
  const { eventInfo } = useEventInfo();

  const [isSm2orLarge] = useResponsive({ values: { _: false, sm2: true } });
  const [isGuestMobileFooterNavEnabled] = useIsFeatureEnabled('guestMobileFooterNavEnabled', { skip: isSm2orLarge });

  // Drops migrated legacy pages, plus the RSVP page when it is effectively disabled or past its deadline
  const { pages: filteredPages } = useGuestSitePages(pages, event.hasEnabledLegacyRsvpQuestions);
  const { eventDesign } = event;
  const isMobileOrTablet = useIsMobileOrTablet();
  const { isCustomArtEnabled, customArtUrl } = useMemo(
    () => ({
      isCustomArtEnabled: getIsCustomArtEnabled(eventDesign, isMobileOrTablet),
      customArtUrl: isMobileOrTablet ? addRendition({ url: getCustomArtMobileUrl(eventDesign), renditionSize: 'medium' }) : getCustomArtUrl(eventDesign, DesignLayoutType.aloha),
    }),
    [eventDesign, isMobileOrTablet],
  );

  const { isSmallScreen, loading, CustomStyle } = useAlohaDesign(eventHandle, event.eventDesign);
  // PRNT-4898 bundled path: theme art rendered as React; inert unless the flag is 'on'.
  const simpleCanvas = useAlohaSimpleCanvas();
  // Fetch header settings for preload URL partitioning. The query is shared with the WebsiteHeader
  // component (Apollo deduplicates by query+variables), so this adds no extra network request.
  const { websiteHeader: alohaWebsiteHeader } = useWebsiteHeaderSettings({ targetWebsiteLayout: 'aloha', eventHandle });
  // useMediaQuery returns false during SSR (no window.matchMedia).
  // For mobile SSR, assume portrait orientation — most mobile devices are portrait on initial load.
  // The client will correct this on hydration if the device is actually landscape.
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const useMobilePhoto = isMobile ? withWindow(() => isPortrait && isMobile, true) : false;
  const homeRef = useRef<HTMLDivElement>(null);
  const isPreviewing = isInIframe();
  const containerRef = useRef(null);
  // Must be referentially stable so useAlohaScroll's effect doesn't re-run
  // on every render. Brannan's LayoutBrannanSinglePage does this correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pagesRef = useMemo(() => ({}), []) as Record<string, PagesRefs>;
  // Stable per-page ref callback. An inline `ref={node => ...}` is a fresh function on every
  // render, so React detaches and re-attaches every section's ref on every commit, and each
  // re-attach wrote a brand new { ref, page } entry. useAlohaScroll reads those entries, so an
  // unchanged page list looked changed and re-rendered the whole layout.
  const pagesById = useMemo(() => new Map(filteredPages.map(page => [page.id, page])), [filteredPages]);
  const pagesByIdRef = useRef(pagesById);
  pagesByIdRef.current = pagesById;
  const pageRefSetters = useRef(new Map<string, (node: HTMLDivElement | null) => void>());
  const setPageRef = useCallback(
    (pageId: string) => {
      const cached = pageRefSetters.current.get(pageId);
      if (cached) return cached;
      const setter = (node: HTMLDivElement | null) => {
        const page = pagesByIdRef.current.get(pageId);
        if (page) {
          pagesRef[pageId] = { ref: node, page };
          return;
        }
        // Removal: the page left `filteredPages` during the render that precedes this unmount, so
        // the lookup above misses and the entry would keep a detached node. `useAlohaScroll` and
        // `usePageScrollAtTheBottom` guard on `ref` being falsy, not on the node being attached, so
        // a detached node reads as a live section with an all-zero rect. Null it in place — the
        // inline ref this cache replaced did the same on unmount.
        const stale = pagesRef[pageId];
        if (stale) {
          stale.ref = null;
        }
        pageRefSetters.current.delete(pageId);
      };
      pageRefSetters.current.set(pageId, setter);
      return setter;
    },
    [pagesRef],
  );
  // The cached setters only run when a section mounts or unmounts, so a page that keeps its id
  // through an edit — a slug rename in the editor preview — would leave the snapshot taken at mount
  // behind, and the scroll spy writes `page.pageSlug` from these entries back into the URL. Refresh
  // in place rather than replacing the entry: `useAlohaScroll` compares entries by reference to
  // decide whether the page list changed, so a new object here would re-render every section.
  filteredPages.forEach(page => {
    const entry = pagesRef[page.id];
    if (entry && entry.page !== page) {
      entry.page = page;
    }
  });
  const { pageColor, pageFontColor, artBackgroundColor, artBackgroundFontColor } = useColorPaletteProvider();
  const { checkIsWelcomePage, isWelcomePageAsCustomPageEnabled } = useWelcomePage();
  // Section the guest landed on — it gets a viewport-filling floor so its late-arriving
  // widgets can't displace the section below it. PRNT-4799.
  const deepLinkTargetSlug = useDeepLinkTargetSlug(eventHandle);
  // Resolve to ONE page id, not a slug: mid-migration events carry a legacy page AND its migrated
  // custom twin under the same slug (both rendered — pre-existing behaviour when the per-slug
  // migration flag is off). Matching by slug decorated BOTH copies, which gave scroll anchoring
  // two legal sentinels a section apart; re-anchoring between them threw the viewport a full
  // viewport-height (~1.0 CLS). First match wins. PRNT-4799.
  const deepLinkTargetPageId = useMemo(
    () => (deepLinkTargetSlug ? filteredPages.find(p => isDeepLinkTargetSlug(p.pageSlug, deepLinkTargetSlug))?.id : undefined),
    [filteredPages, deepLinkTargetSlug],
  );

  // Batched reveal: while sections load, only the welcome page and the landed-on section
  // participate in layout — the rest keep fetching but render display:none, then join in ONE
  // commit when the provider reveals (target loaded, or its 3s fallback). Sections growing at
  // unpredictable times made the landing scroll's measurement stale (the viewport painted one
  // section past the target — measured as a deterministic 0.97 CLS) and every growth above the
  // viewport was a potential shift. With one insertion commit, the layout effect below restores
  // the target's offset before paint, so nothing visible ever moves. Preview iframes are
  // exempt (the editor needs all sections interactable immediately). PRNT-4799.
  const shouldReveal = useShouldRevealPageContent();
  const isSectionDeferred = useCallback(
    (page: EventPageFragment): boolean => {
      // Deferral protects a deep-link landing from stale measurements. On a root route there is no
      // target (and so no viewport floor), the landing is simply the top, and the reveal would
      // instead insert sections into view below a short welcome page — measured 0.25 CLS. Nothing
      // to protect, so don't defer. PRNT-4799.
      if (!deepLinkTargetPageId) return false;
      if (isPreviewing) return false;
      if (checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage) return false;
      return !shouldReveal({ type: page.type, pageSlug: page.pageSlug });
    },
    [deepLinkTargetPageId, isPreviewing, checkIsWelcomePage, shouldReveal],
  );
  const allSectionsRevealed = useMemo(() => filteredPages.every(page => !isSectionDeferred(page)), [filteredPages, isSectionDeferred]);

  // Document offset of the deep-link target, tracked while sections are deferred so the reveal
  // commit can restore it. Updated every pre-reveal commit (layout effect), consumed once.
  const preRevealTargetDocYRef = useRef<number | null>(null);
  const revealCompensationDoneRef = useRef(false);
  useIsomorphicLayoutEffect(() => {
    const container = containerRef.current as HTMLDivElement | null;
    if (!container || revealCompensationDoneRef.current) return;
    const targetRef = deepLinkTargetPageId ? pagesRef[deepLinkTargetPageId]?.ref : null;
    if (!targetRef) return;
    const targetDocY = targetRef.getBoundingClientRect().top + container.scrollTop;
    if (!allSectionsRevealed) {
      preRevealTargetDocYRef.current = targetDocY;
      return;
    }
    revealCompensationDoneRef.current = true;
    if (preRevealTargetDocYRef.current === null) return;
    // Where scroll anchoring exists it already compensated the insertion in-layout (the target is
    // the only anchor candidate); adding a manual correction on top double-counts — measured as
    // the viewport landing ~1269px past the target at reveal. Manual path is for Safari only.
    if (isScrollAnchoringSupported()) return;
    const insertedAbove = targetDocY - preRevealTargetDocYRef.current;
    // Same commit as the insertion, before paint: the guest's position is preserved exactly, so
    // the reveal frame contains growth and an equal scroll — no visible movement.
    if (Math.abs(insertedAbove) > 1) {
      container.scrollTop += insertedAbove;
    }
  });
  const scrollPages = useMemo(() => {
    if (!hideNav) return filteredPages;
    return filteredPages.filter(page => !checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
  }, [filteredPages, hideNav, checkIsWelcomePage]);

  const hasWelcomePage = useMemo(() => filteredPages.some(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage), [filteredPages, checkIsWelcomePage]);

  const {
    handleMenuClicked,
    handleWelcomeRSVPButtonClicked,
    handleWelcomeViewDetailsButtonClicked,
    handleFooterButtonClicked,
    handleMenuOpenedChange,
    handleWelcomeOnNotYouButtonClicked,
    handleWelcomeOnNotYouErrorRequest,
    telemetryEditPhotoActions,
  } = useLayoutAlohaController({ eventHandle, pages: filteredPages });

  // Create eventProps with filtered pages for navigation menus
  const eventMenuProps = useMemo(() => ({ ...getEventMenuPropsFromEvent(event), pages: filteredPages }), [event, filteredPages]);

  const { tq } = useTranslation('joykit');

  // Get website banner offset
  const { bannerOffset } = useWebsiteBannerOffset();

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  // CORE-6488: Legacy slug redirect for Aloha layout.
  // Same logic as LayoutBrannan — when a URL has a pre-migration slug (e.g. /event/weddingparty),
  // it won't match any page in filteredPages (filtered out by useFilterMigratedLegacyPages).
  // Redirect to the canonical URL so the scroll-to-page logic finds the right section.
  const { currentPageSlug } = useCurrentPageSlug(eventHandle);
  // Resolved here, not inside each Body: reading the URL per section subscribed all of them to the
  // router, so every scroll-spy URL rewrite re-rendered the whole site. Passed down as a boolean,
  // it only changes for the two sections that gained or lost the slug.
  const isStaysRedesignActive = useIsStaysRedesignActive();
  const history = useHistory();
  const queryParams = useQueryParams();
  const eventPageRoute = useEventPageRoute(eventHandle, filteredPages);

  useEffect(() => {
    if (!currentPageSlug) return;

    // Check if the slug resolves in the filtered pages
    const matchesFiltered = filteredPages.some(p => p.pageSlug.toLocaleLowerCase() === currentPageSlug.toLocaleLowerCase());
    if (matchesFiltered) return;

    // Slug doesn't match filtered pages — check the unfiltered list for a legacy page
    const legacyPage = pages.find(p => p.pageSlug.toLocaleLowerCase() === currentPageSlug.toLocaleLowerCase());
    if (!legacyPage) return;

    const canonicalSlug = getMigratedPageSlug(legacyPage.type) ?? toCanonicalMigratedSlug(legacyPage.pageSlug);
    if (!canonicalSlug || canonicalSlug === currentPageSlug) return;

    const livePage = filteredPages.find(p => p.pageSlug === canonicalSlug || toCanonicalMigratedSlug(p.pageSlug) === canonicalSlug);
    const canonicalPath = livePage ? eventPageRoute.goToPath(livePage.type, livePage.pageSlug) : `/${eventHandle}/${canonicalSlug}`;

    if (canonicalPath && !canonicalPath.endsWith(`/${currentPageSlug}`)) {
      history.replace(
        queryString.stringifyUrl({
          url: canonicalPath,
          query: queryParams,
        }),
      );
    }
  }, [currentPageSlug, pages, filteredPages, eventHandle, eventPageRoute, history, queryParams]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, event.eventDesign?.theme.websiteLayoutAlohaSettings?.alohaColorPreference?.supportsCustomization]);

  const onEditThemeColorClick = useCallback(() => {
    // send the message to the parent window to open the theme editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editThemeColor',
        inlineEditData: {
          imageURL: filteredPages.find(p => checkIsWelcomePage(p.pageSlug, p.type).isWelcomePage)?.photoV2?.url || '',
          eventDesignId: event.eventDesign?.id || '',
        },
      },
    });
  }, [event.eventDesign?.id, filteredPages, checkIsWelcomePage]);

  const welcomePhotoUrl = useWelcomePagePhotoUrl({ event, headerPhoto: alohaWebsiteHeader?.headerPhoto });

  const indexOfValidPageWidgetAfterWelcomePage = useMemo(() => {
    const indexOfWelcome = filteredPages.findIndex(p => {
      const { isWelcomePage } = checkIsWelcomePage(p.pageSlug, p.type);
      return isWelcomePage;
    });
    // if welcome page is not found, then we don't need to disable the top margin
    if (indexOfWelcome === -1) {
      return -1;
    }
    const indexOfFirstValidPageAfterWelcome = filteredPages.slice(indexOfWelcome + 1).findIndex(p => pageWidgets[p.type as PreparedPages]);

    // Return -1 if no valid page found, otherwise calculate the actual index
    return indexOfFirstValidPageAfterWelcome === -1 ? -1 : indexOfFirstValidPageAfterWelcome + indexOfWelcome + 1;
  }, [filteredPages, checkIsWelcomePage]);

  return (
    <MobileTopBarNavOffsetProvider onMenuOpenChange={handleMenuOpenedChange}>
      <FrameWrapper>
        {CustomStyle && <CustomStyle />}
        <PreloadImages
          pages={filteredPages}
          eventHandle={eventHandle}
          headerPhoto={alohaWebsiteHeader?.headerPhoto}
          fallbackPhotoUrl={photo?.url}
        />
        <AuxFrameProvider
          isSmallScreen={isSmallScreen}
          eventId={id}
          primaryPhoto={useMobilePhoto ? mobilePhoto : photo}
          pages={filteredPages}
          isPreviewing={isPreviewing}
        >
          <StyledFlexAloha
            ref={containerRef}
            loading={loading}
            isPreviewing={isPreviewing}
            className="aloha-scrollable"
            id={ALOHA_SCROLL_CONTAINER_ID}
            heightOffset={bannerOffset}
          >
            {!hideNav && <GuestLanguageSelectorFloating />}
            <div
              id="photo-welcome"
              ref={homeRef}
            />
            {!isMobile && !hideNav && (
              <WebsiteHeader
                eventMenuProps={eventMenuProps}
                artBackgroundFontColor={artBackgroundFontColor}
                telemetryEditPhotoActions={telemetryEditPhotoActions}
                handleMenuOpenedChange={handleMenuOpenedChange}
                handleMenuClicked={handleMenuClicked}
                eventDisplayName={eventDisplayName}
                greeting={greeting}
              />
            )}
            {/* The mobile menu normally renders inside the welcome page header. */}
            {isMobile && !hideNav && !hasWelcomePage && (
              <TopBarNavEventMenu
                eventProps={eventMenuProps}
                handleMenuOpenedChange={handleMenuOpenedChange}
                handleMenuClicked={handleMenuClicked}
                title={eventDisplayName}
                eventHandle={eventHandle}
                backgroundColor={pageColor}
              />
            )}
            {!hideNav && (
              <MobileTopBarNav
                eventHandle={eventHandle}
                pages={eventMenuProps.pages}
              />
            )}
            <MainFrame backgroundColor={pageColor}>
              <BulkCustomPageDataProvider
                eventHandle={eventHandle}
                pages={filteredPages}
                eventPages={pages}
              >
                {filteredPages.map((page, idx) => {
                  const { isWelcomePage } = checkIsWelcomePage(page.pageSlug, page.type);

                  if (hideNav && isWelcomePage) {
                    return null;
                  }

                  if (isWelcomePage) {
                    const isCustomVariant = isWelcomePageAsCustomPageEnabled && page.type === EventPageType.custom;

                    return (
                      <PageContainer
                        id={page.pageSlug}
                        backgroundColor={artBackgroundColor}
                        textColor={artBackgroundFontColor}
                        key={page.id}
                        ref={setPageRef(page.id)}
                        disableBottomMargin
                      >
                        <div>
                          {isMobile && !hideNav && (
                            <WebsiteHeader
                              eventMenuProps={eventMenuProps}
                              artBackgroundFontColor={artBackgroundFontColor}
                              telemetryEditPhotoActions={telemetryEditPhotoActions}
                              handleMenuOpenedChange={handleMenuOpenedChange}
                              handleMenuClicked={handleMenuClicked}
                              eventDisplayName={eventDisplayName}
                              greeting={greeting}
                            />
                          )}
                          {isCustomVariant ? (
                            <Box position="relative">
                              {/* Art layer: clips the bottom-anchored art (CustomArt / `simple` sprite) to the
                                welcome page so it can't bleed into the next page. Kept in its own absolute
                                layer — not on the outer wrapper — so Body's page InlineEditor label/toolbar
                                (positioned above the page at negative offsets) aren't clipped.
                                `overflow: hidden` (not `clip`) for iOS <16 / older-Android webview support. */}
                              <Box
                                position="absolute"
                                top={0}
                                right={0}
                                bottom={0}
                                left={0}
                                overflow="hidden"
                                pointerEvents="none"
                                // Keep the art layer behind the content: absolute siblings otherwise
                                // paint over the non-positioned Body, and `simple.prop` is opaque.
                                zIndex={0}
                                {...(!isCustomArtEnabled ? simple.prop : {})}
                                {...(!isCustomArtEnabled ? themeCanvasBoxProps(simpleCanvas) : {})}
                              >
                                {/* PRNT-4898 bundled path: this art layer is the simple-canvas
                                  owner on the custom variant. */}
                                {!isCustomArtEnabled && simpleCanvas.sprites.length > 0 && (
                                  <ThemeSprites
                                    sprites={simpleCanvas.sprites}
                                    context={ALOHA_SIMPLE_CONTEXT}
                                  />
                                )}
                                {isCustomArtEnabled && <CustomArt backgroundImageUrl={customArtUrl} />}
                              </Box>
                              <Box
                                position="relative"
                                zIndex={1}
                              >
                                <Body
                                  key={page.id}
                                  data-testid={`layout-body-${page.pageSlug}`}
                                  page={page}
                                  event={event}
                                  eventHandle={eventHandle}
                                  pageTitle={page.pageTitle}
                                  isWelcomePage={true}
                                  position="relative"
                                />
                              </Box>
                            </Box>
                          ) : (
                            <InlineEditor
                              elementLabel={tq`Theme`}
                              wrapperType="actionInside"
                              actionData={{
                                editThemeColor: onEditThemeColorClick,
                              }}
                              componentName="welcomePageTheme"
                              pageName={page.type}
                              pageSlug={page.pageSlug}
                              wrapperCSS={{ zIndex: 'docked' }}
                              // No longer preventing user from choosing an art background color
                              isEligibleForInlineEditing={true}
                            >
                              <WelcomeCard
                                homeRef={homeRef}
                                page={page}
                                eventHandle={eventHandle}
                                hideEventCountdown={eventInfo?.settings.hideEventCountdown ?? false}
                                finalizedDate={event.info.finalizedDate}
                                location={event.info.location}
                                pages={filteredPages}
                                onRSVPButtonClicked={handleWelcomeRSVPButtonClicked}
                                onViewDetailsButtonClicked={handleWelcomeViewDetailsButtonClicked}
                                onNotYouButtonClicked={handleWelcomeOnNotYouButtonClicked}
                                onNotYouErrorRequest={handleWelcomeOnNotYouErrorRequest}
                                isCustomArtEnabled={isCustomArtEnabled}
                                customArtUrl={customArtUrl}
                                {...restProps}
                              />
                            </InlineEditor>
                          )}
                          {video ? (
                            <LazyWelcomeVideo
                              key="welcome-video"
                              video={video}
                              thumbnailUrl={video.thumbnailUrl || welcomePhotoUrl}
                            />
                          ) : null}
                        </div>
                      </PageContainer>
                    );
                  }

                  // Non-welcome pages are below the fold — defer to client rendering
                  if (page.type === 'custom' && page?.pageSlug === 'accommodations') {
                    return (
                      <CustomPageContainer
                        key={page.id}
                        id={page.pageSlug}
                        style={isSectionDeferred(page) ? { display: 'none' } : undefined}
                        backgroundColor={pageColor}
                        textColor={pageFontColor}
                        ref={setPageRef(page.id)}
                        disableMobileTopMargin={idx === indexOfValidPageWidgetAfterWelcomePage}
                        $fillViewport={page.id === deepLinkTargetPageId}
                      >
                        {page.id === deepLinkTargetPageId && (
                          <div
                            data-scroll-anchor-sentinel
                            aria-hidden="true"
                            // visibility: Brannan's frame hides its children with `visibility: hidden` while the style
                            // applicator loads, and scroll anchoring cannot select an invisible node — leaving NO legal
                            // anchor for the whole load window, which is why single-page shifted where Aloha didn't.
                            // visibility is overridable by descendants, so re-declaring it here restores the sentinel as
                            // a candidate. 1px and empty, so nothing is visible. PRNT-4799.
                            style={{ height: 1, marginBottom: -1, visibility: 'visible' }}
                          />
                        )}
                        <Body
                          data-testid={`layout-body-${page.pageSlug}`}
                          page={page}
                          event={event}
                          eventHandle={eventHandle}
                          applicatorProps={simple.prop || EMPTY_APPLICATOR_PROPS}
                          isStaysRedesignCurrentPage={isStaysRedesignActive && page.pageSlug === currentPageSlug}
                          pageTitle={page.pageTitle}
                        />
                      </CustomPageContainer>
                    );
                  }

                  if (!pageWidgets[page.type as PreparedPages]) {
                    return null;
                  }

                  return (
                    <PageContainer
                      key={page.id}
                      id={page.type}
                      style={isSectionDeferred(page) ? { display: 'none' } : undefined}
                      backgroundColor={pageColor}
                      textColor={pageFontColor}
                      ref={setPageRef(page.id)}
                      disableMobileTopMargin={idx === indexOfValidPageWidgetAfterWelcomePage}
                      $fillViewport={page.id === deepLinkTargetPageId}
                    >
                      {page.id === deepLinkTargetPageId && (
                        <div
                          data-scroll-anchor-sentinel
                          aria-hidden="true"
                          // visibility: Brannan's frame hides its children with `visibility: hidden` while the style
                          // applicator loads, and scroll anchoring cannot select an invisible node — leaving NO legal
                          // anchor for the whole load window, which is why single-page shifted where Aloha didn't.
                          // visibility is overridable by descendants, so re-declaring it here restores the sentinel as
                          // a candidate. 1px and empty, so nothing is visible. PRNT-4799.
                          style={{ height: 1, marginBottom: -1, visibility: 'visible' }}
                        />
                      )}
                      <Body
                        data-testid={`layout-body-${page.pageSlug}`}
                        page={page}
                        event={event}
                        eventHandle={eventHandle}
                        applicatorProps={simple.prop || EMPTY_APPLICATOR_PROPS}
                        isStaysRedesignCurrentPage={isStaysRedesignActive && page.pageSlug === currentPageSlug}
                        pageTitle={page.pageTitle}
                      />
                    </PageContainer>
                  );
                })}
              </BulkCustomPageDataProvider>
              <NoSsr>
                {/* The footer is content-stable while sections load, which made it a preferred
                    scroll-anchor — Chrome pinned IT as the target grew, sliding the viewport past
                    the target. See scrollAnchorCandidacy. PRNT-4799. */}
                <FooterAnchorExcludedFlex __css={alohaFooterContainerStyles}>
                  {/* FooterNav is only visible on mobile */}
                  {isGuestMobileFooterNavEnabled && !hideNav && (
                    <Flex
                      width="100%"
                      display={{ _: 'flex', sm2: 'none' }}
                    >
                      <FooterNav
                        pages={filteredPages}
                        eventHandle={eventHandle}
                        eventDisplayName={eventDisplayName}
                        handleMenuClicked={handleMenuClicked}
                      />
                    </Flex>
                  )}
                  <Footer
                    onButtonClicked={handleFooterButtonClicked}
                    data-testid={'layout-footer'}
                  />
                </FooterAnchorExcludedFlex>
              </NoSsr>
            </MainFrame>
          </StyledFlexAloha>
          {/* Renders null; see AlohaScrollController for why it is a child rather than a hook here. */}
          <AlohaScrollController
            pages={scrollPages}
            pagesRef={pagesRef}
            container={containerRef.current}
            setIsLastPageVisible={setIsLastPageVisible}
            setIsPastFirstPage={setIsPastFirstPage}
          />
        </AuxFrameProvider>
      </FrameWrapper>
    </MobileTopBarNavOffsetProvider>
  );
};

LayoutAloha.displayName = 'LayoutAloha';

export { LayoutAloha };
